import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from '../../../i18n/i18n';
import { type AbortVariant, type FlowAction } from '../../../types';
import {
  getAbortVariant,
  abortVariantConfig,
} from '../../../utils/abortVariants';
import HostedStatusBlock from '../../../components/hosted/StatusBlock';
import HostedActionRow from '../../../components/hosted/ActionRow';

type Props = {
  errorCode?: number;
  variant?: AbortVariant;
  onReject?: () => void;
  onRestart?: () => void;
  onBack?: () => void;
  /** Button direction: column on mobile, row on desktop */
  buttonDirection?: 'col' | 'row';
};

export default function HostedAbortScreen({
  errorCode,
  variant: variantProp,
  onReject,
  onRestart,
  onBack,
  buttonDirection = 'row',
}: Props) {
  const { t } = useTranslation();
  const variant = variantProp ?? getAbortVariant(errorCode);
  const config = abortVariantConfig[variant];

  const secondary: FlowAction | undefined = onReject
    ? { label: t('abort.leave'), onClick: onReject }
    : undefined;

  let primary: FlowAction | undefined;
  if (config.showRestart && onRestart) {
    primary = {
      label: (
        <>
          <FontAwesomeIcon icon={faArrowsRotate} className="size-4 mr-2" />
          {t(config.restartKey)}
        </>
      ),
      onClick: onRestart,
    };
  } else if (variant === 'appError' && onBack) {
    primary = { label: t('abort.selectDifferentApp'), onClick: onBack };
  }

  return (
    <HostedStatusBlock
      visual={
        <div
          className={`flex items-center justify-center size-12 rounded-full ${config.bgClass}`}
        >
          <FontAwesomeIcon
            icon={config.icon}
            className={`size-7 ${config.iconClass}`}
          />
        </div>
      }
      title={t(config.titleKey)}
      subtitle={t(config.subtitleKey)}
    >
      {(primary || secondary) && (
        <HostedActionRow
          className="mt-2 w-full"
          direction={buttonDirection}
          pill={false}
          primary={primary}
          secondary={secondary}
        />
      )}
    </HostedStatusBlock>
  );
}
